import { supabase } from "@/integrations/supabase/client";

// Enhanced cache for validation and role results
const validationCache = new Map<string, { result: boolean; timestamp: number }>();
const roleCache = new Map<string, { role: string | null; timestamp: number }>();

export interface UserProfile {
  id: string;
  user_id: string;
  full_name?: string;
  phone?: string;
  role: 'customer' | 'restaurant_owner' | 'admin';
  created_at: string;
  updated_at: string;
}

export interface UserAddress {
  id: string;
  user_id: string;
  street: string;
  number: string;
  complement?: string;
  neighborhood: string;
  is_default: boolean;
}

class UserService {
  async getCurrentUserProfile(): Promise<UserProfile | null> {
    try {
      // First validate profile integrity
      const isIntegrityValid = await this.validateProfileIntegrity();
      if (!isIntegrityValid) {
        console.error('Profile integrity check failed');
        return null;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      // Use secure function that includes audit logging
      const { data, error } = await supabase
        .rpc('get_profile_secure', { target_user_id: user.id });

      if (error) {
        if (error.code === 'PGRST116') return null;
        throw error;
      }
      
      if (!data || data.length === 0) return null;
      
      const profile = data[0];
      return {
        id: profile.user_id,
        user_id: profile.user_id,
        full_name: profile.full_name,
        phone: profile.phone,
        role: profile.role as 'customer' | 'restaurant_owner' | 'admin',
        created_at: profile.created_at,
        updated_at: profile.updated_at
      } as UserProfile;
    } catch (error) {
      console.error('Error in getCurrentUserProfile:', error);
      return null;
    }
  }

  async validateProfileIntegrity(): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.id) return false;
      
      // Check cache first (5 minute cache)
      const cacheKey = `profile_integrity_${user.id}`;
      const cached = validationCache.get(cacheKey);
      const now = Date.now();
      
      if (cached && (now - cached.timestamp) < 5 * 60 * 1000) {
        return cached.result;
      }
      
      const { data, error } = await supabase.rpc('validate_user_profile_integrity');
      
      if (error) {
        console.error('Profile integrity validation error:', error);
        return false;
      }
      
      const result = data === true;
      
      // Cache the result
      validationCache.set(cacheKey, { result, timestamp: now });
      
      return result;
    } catch (error) {
      console.error('Profile integrity validation failed:', error);
      return false;
    }
  }

  async getCurrentUserRole(): Promise<string | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.id) return null;
      
      // Session cache for faster user switching (5 minutes)
      const cacheKey = user.id;
      const cached = roleCache.get(cacheKey);
      const now = Date.now();
      
      if (cached && (now - cached.timestamp) < 5 * 60 * 1000) {
        return cached.role;
        return cached.role;
      }
      
      // Use direct query instead of RPC for reliability
      const { data, error } = await supabase
        .from('profiles')
        .select('role')
        .eq('user_id', user.id)
        .single();
      
      if (error || !data) {
        console.warn('Profile not found, checking user metadata:', error);
        // Fallback para metadados do usuário
        const metadataRole = user.user_metadata?.user_role;
        const fallbackRole = metadataRole || 'customer';
        
        // Cache o fallback se válido
        if (fallbackRole) {
          roleCache.set(cacheKey, { role: fallbackRole, timestamp: now });
        }
        
        return fallbackRole;
      }
      
      const role = data.role as string;
      
      // Cache valores válidos
      if (role) {
        roleCache.set(cacheKey, { role, timestamp: now });
      }
      
      return role;
    } catch (error) {
      console.error('Error in getCurrentUserRole:', error);
      // Fallback final para customer
      return 'customer';
    }
  }

  async getUserAddresses(userId: string): Promise<UserAddress[]> {
    // Use secure function with proper access control
    const { data, error } = await supabase
      .rpc('get_user_addresses_secure', { target_user_id: userId });

    if (error) throw error;
    return data as UserAddress[];
  }

  async getDefaultAddress(userId: string): Promise<UserAddress | null> {
    // Verify user can access this data
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;
    
    // Only allow access to own data or admin access
    if (user.id !== userId) {
      const { data: role } = await supabase.rpc('get_current_user_role');
      if (role !== 'admin') {
        throw new Error('Unauthorized access to user address data');
      }
    }

    const { data, error } = await supabase
      .from('addresses')
      .select('*')
      .eq('user_id', userId)
      .eq('is_default', true)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data as UserAddress;
  }

  async createAddress(address: Omit<UserAddress, 'id'>): Promise<string> {
    // Verify user can only create addresses for themselves
    const { data: { user } } = await supabase.auth.getUser();
    if (!user || user.id !== address.user_id) {
      throw new Error('Unauthorized: Can only create addresses for your own account');
    }

    const { data, error } = await supabase
      .from('addresses')
      .insert([address])
      .select('id')
      .single();

    if (error) throw error;
    
    // Log address creation for security audit
    await supabase.rpc('audit_sensitive_operation', {
      table_name: 'addresses',
      operation: 'address_create',
      record_id: data.id,
      details: { user_id: address.user_id }
    });
    
    return data.id;
  }

  async updateAddress(id: string, updates: Partial<UserAddress>): Promise<void> {
    const { error } = await supabase
      .from('addresses')
      .update(updates)
      .eq('id', id);

    if (error) throw error;
  }

  async deleteAddress(id: string): Promise<void> {
    const { error } = await supabase
      .from('addresses')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  async setDefaultAddress(userId: string, addressId: string): Promise<void> {
    // Remove default from all addresses
    await supabase
      .from('addresses')
      .update({ is_default: false })
      .eq('user_id', userId);

    // Set new default
    const { error } = await supabase
      .from('addresses')
      .update({ is_default: true })
      .eq('id', addressId);

    if (error) throw error;
  }

  formatAddress(address: UserAddress): string {
    return `${address.street}, ${address.number}${address.complement ? `, ${address.complement}` : ''} - ${address.neighborhood}`;
  }
}

export const userService = new UserService();